import { ApplicantType } from "shared/types";
import { MetricBreakdownColumn, MetricModalData } from "./types";
import { ApplicationMetricData, GroupStatMetricNames } from "api/applications";

export const GROUP_STAT_LABEL: Record<GroupStatMetricNames, string> = {
  [GroupStatMetricNames.GrossIncome]: "Gross income",
  [GroupStatMetricNames.NetIncome]: "Net income",
  [GroupStatMetricNames.GrossIncomeToRentRatio]: "Gross income to rent",
  [GroupStatMetricNames.NetIncomeToRentRatio]: "Net income to rent",
  [GroupStatMetricNames.DebtToGrossIncome]: "Debt to gross income",
  [GroupStatMetricNames.DebtToNetIncome]: "Debt to net income",
  [GroupStatMetricNames.SelfReportedGrossIncome]: "Gross income",
  [GroupStatMetricNames.SelfReportedGrossIncomeToRent]: "Gross income to rent",
  [GroupStatMetricNames.DebtToSelfReportedGrossIncome]: "Debt to gross income",
  [GroupStatMetricNames.AverageCreditScore]: "Average credit score",
  [GroupStatMetricNames.MinCreditScore]: "Lowest credit score",
  [GroupStatMetricNames.MaxCreditScore]: "Highest credit score",
  [GroupStatMetricNames.NoCreditScorePeople]: "People with no credit score"
};

export const INCOME_METRICS = [
  GroupStatMetricNames.GrossIncome,
  GroupStatMetricNames.NetIncome,
  GroupStatMetricNames.GrossIncomeToRentRatio,
  GroupStatMetricNames.NetIncomeToRentRatio,
  GroupStatMetricNames.DebtToGrossIncome,
  GroupStatMetricNames.DebtToNetIncome,
  GroupStatMetricNames.SelfReportedGrossIncome,
  GroupStatMetricNames.SelfReportedGrossIncomeToRent,
  GroupStatMetricNames.DebtToSelfReportedGrossIncome
];

export const CREDIT_METRICS = [
  GroupStatMetricNames.AverageCreditScore,
  GroupStatMetricNames.MinCreditScore,
  GroupStatMetricNames.MaxCreditScore,
  GroupStatMetricNames.NoCreditScorePeople
];

export const METRICS_WITH_EMP = [
  GroupStatMetricNames.DebtToGrossIncome,
  GroupStatMetricNames.DebtToNetIncome,
  GroupStatMetricNames.DebtToSelfReportedGrossIncome
];

export const DEFAULT_ROLES = [
  ApplicantType.Applicant,
  ApplicantType.Occupant,
  ApplicantType.CoSigner
];

export const DEFAULT_INCOME_METRIC: Partial<ApplicationMetricData> = {
  metric: GroupStatMetricNames.GrossIncome,
  title: GROUP_STAT_LABEL[GroupStatMetricNames.GrossIncome],
  roles: DEFAULT_ROLES,
  badge: true,
  role_label: true
};

export const DEFAULT_CREDIT_METRIC: Partial<ApplicationMetricData> = {
  metric: GroupStatMetricNames.AverageCreditScore,
  title: GROUP_STAT_LABEL[GroupStatMetricNames.AverageCreditScore],
  roles: DEFAULT_ROLES,
  badge: true,
  role_label: true
};

/** Maps GroupStatMetricNames to their specific modal data */
export const METRIC_MODAL_MAP: Record<GroupStatMetricNames, MetricModalData> = {
  [GroupStatMetricNames.GrossIncome]: {
    title: "Verified gross income",
    warningText:
      "Some applicants don't have a verified gross income and were not included in the calculation",
    detailText:
      "Combined verified gross monthly income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.GrossIncome,
      MetricBreakdownColumn.VerifiedBadge
    ]
  },
  [GroupStatMetricNames.NetIncome]: {
    title: "Verified net income",
    warningText:
      "Some applicants don't have a verified net income and were not included in the calculation",
    detailText:
      "Combined verified net monthly income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.NetIncome,
      MetricBreakdownColumn.VerifiedBadge
    ]
  },
  [GroupStatMetricNames.GrossIncomeToRentRatio]: {
    title: "Verified gross income to rent",
    warningText:
      "Some applicants didn't have a verified gross income and were not included in the calculation",
    detailText:
      "Rent as a percentage of combined verified gross monthly income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.VerifiedGrossIncome,
      MetricBreakdownColumn.VerifiedBadge
    ]
  },
  [GroupStatMetricNames.NetIncomeToRentRatio]: {
    title: "Verified net income to rent",
    warningText:
      "Some applicants didn't have a verified net income and were not included in the calculation",
    detailText:
      "Rent as a percentage of combined verified net monthly income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.NetIncome,
      MetricBreakdownColumn.VerifiedBadge
    ]
  },
  [GroupStatMetricNames.DebtToGrossIncome]: {
    title: "Debt to verified gross income",
    warningText:
      "Some applicants didn't have a verified gross income or monthly debt and were not included in the calculation",
    detailText:
      "Monthly debt as a percentage of combined verified gross income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.MonthlyDebt,
      MetricBreakdownColumn.VerifiedGrossIncome
    ]
  },
  [GroupStatMetricNames.DebtToNetIncome]: {
    title: "Debt to verified net income",
    warningText:
      "Some applicants didn't have a verified net income or monthly debt and were not included in the calculation",
    detailText:
      "Monthly debt as a percentage of combined verified net income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.MonthlyDebt,
      MetricBreakdownColumn.VerifiedNetIncome
    ]
  },
  [GroupStatMetricNames.SelfReportedGrossIncome]: {
    title: "Self-reported gross income",
    warningText:
      "Some applicants didn't provide a self-reported gross income and were not included in the calculation",
    detailText:
      "Combined self-reported gross monthly income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.SelfReportedGrossIncome,
      MetricBreakdownColumn.SelfReportedBadge
    ]
  },
  [GroupStatMetricNames.SelfReportedGrossIncomeToRent]: {
    title: "Self-reported gross income to rent",
    warningText:
      "Some applicants didn't have a self-reported gross income and were not included in the calculation",
    detailText: "Monthly rent compared to total self-reported gross income",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.SelfReportedGrossIncome,
      MetricBreakdownColumn.SelfReportedBadge
    ]
  },
  [GroupStatMetricNames.DebtToSelfReportedGrossIncome]: {
    title: "Debt to self-reported gross income",
    warningText:
      "Some applicants didn't have a self-reported gross income or monthly debt and were not included in the calculation",
    detailText:
      "Monthly debt as a percentage of the combined self-reported gross income for applicants added to this calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.MonthlyDebt,
      MetricBreakdownColumn.SelfReportedGrossIncome
    ]
  },
  [GroupStatMetricNames.AverageCreditScore]: {
    title: "Average credit score",
    warningText:
      "Some applicants don't have a credit score and were not included in the calculation",
    detailText:
      "Average credit score of all applicants included in the calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.CreditScore,
      MetricBreakdownColumn.ViewCreditReport
    ]
  },
  [GroupStatMetricNames.MinCreditScore]: {
    title: "Lowest credit score",
    detailText:
      "The lowest credit score among all applicants included in the calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.CreditScore,
      MetricBreakdownColumn.ViewCreditReport
    ]
  },
  [GroupStatMetricNames.MaxCreditScore]: {
    title: "Highest credit score",
    detailText:
      "The highest credit score among all applicants included in the calculation",
    columns: [
      MetricBreakdownColumn.User,
      MetricBreakdownColumn.CreditScore,
      MetricBreakdownColumn.ViewCreditReport
    ]
  },
  [GroupStatMetricNames.NoCreditScorePeople]: {
    title: "People with no credit score",
    detailText: "Applicants who don't have a credit score",
    columns: [MetricBreakdownColumn.User, MetricBreakdownColumn.ViewApplication]
  }
};

export const METRIC_PLACEHOLDER_VALUES: Record<GroupStatMetricNames, string> = {
  [GroupStatMetricNames.GrossIncome]: "$12,100.00",
  [GroupStatMetricNames.NetIncome]: "$7,690.00",
  [GroupStatMetricNames.GrossIncomeToRentRatio]: "3.21x",
  [GroupStatMetricNames.NetIncomeToRentRatio]: "2.14x",
  [GroupStatMetricNames.DebtToGrossIncome]: "55%",
  [GroupStatMetricNames.DebtToNetIncome]: "65.77%",
  [GroupStatMetricNames.SelfReportedGrossIncome]: "$4,000.00",
  [GroupStatMetricNames.SelfReportedGrossIncomeToRent]: "4.10x",
  [GroupStatMetricNames.DebtToSelfReportedGrossIncome]: "55.55%",
  [GroupStatMetricNames.AverageCreditScore]: "701",
  [GroupStatMetricNames.MinCreditScore]: "543",
  [GroupStatMetricNames.MaxCreditScore]: "780",
  [GroupStatMetricNames.NoCreditScorePeople]: "1"
};
